/**
 * PushSelfTestModal.jsx — Admin "kendine test bildirimi gönder" + push durum teşhisi.
 *
 * Bağlam (2026-08-04 görev talimatı): admin panelden gönderilen toplu
 * bildirimler in-app kaydı oluşturuyordu ama push atmıyordu — backend
 * fanout zinciri düzeltildi (bkz. backend/routes/notifications.py::
 * notify_test_self, backend/routes/push_tokens.py::push_token_status).
 * Bu modal admin'in KENDİ hesabı üzerinde o zinciri uçtan uca doğrulamasını
 * sağlar — toplu bildirim göndermeden önce "push gerçekten çalışıyor mu"
 * sorusuna kendi başına cevap bulabilir.
 *
 * İki bölüm:
 *   1) Push durum özeti — GET /push-tokens/status (herhangi bir authenticated
 *      kullanıcı kendi durumunu sorgulayabilir; TOKEN'IN KENDİSİ ASLA
 *      dönmez/gösterilmez — sadece sayı/platform/tarih).
 *   2) Kendine test bildirimi formu — POST /admin/notify/test-self
 *      (admin only, rate limit 10/saat). Hedef HER ZAMAN çağıran admin'in
 *      kendisidir — request'te user_id alanı YOK (IDOR guard, backend'de
 *      de kabul edilmiyor).
 *
 * Aynı sayfadaki "Toplu Bildirim" modalının (AdminStudentOps.js) hemen
 * yanına bir araç çubuğu butonuyla açılır — admin, gerçek toplu duyuruyu
 * göndermeden önce zinciri kendi cihazında test edebilir.
 *
 * NOT (i18n): Bu dosya, aynı klasördeki components/admin/SmsSendModal.jsx
 * ve pages/AdminStudentOps.js ile AYNI yerel `tr(trText, enText)` ikili-dil
 * deseni kullanır — bu admin CRUD modallerinin baskın konvansiyonu
 * (LanguageContext t()-anahtar kayıt defteri değil). Her iki string de HER
 * ZAMAN birlikte yazılır; tek dilli/hardcoded metin YOKTUR.
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Smartphone,
  Send,
  Loader2,
  X,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  RefreshCw,
  BellRing,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { isSafeInternalDeeplink } from '../../utils/deeplink';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none transition-colors disabled:opacity-60';

export default function PushSelfTestModal({ open, onOpenChange }) {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const tr = (trText, enText) => (lang === 'tr' ? trText : enText);

  // ── Push durumu ────────────────────────────────────────────────────────────
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(false);

  const fetchStatus = useCallback(() => {
    setStatusLoading(true);
    setStatusError(false);
    axios
      .get(`${API}/push-tokens/status`, { headers: authHeaders() })
      .then((r) => setStatus(r.data))
      .catch(() => setStatusError(true))
      .finally(() => setStatusLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setTitle('');
    setMessage('');
    setDeeplink('');
    fetchStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Test formu ─────────────────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [deeplink, setDeeplink] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { push_attempt_reason, active_device_count, ... }
  const [rateLimited, setRateLimited] = useState(false);

  const deeplinkTrimmed = deeplink.trim();
  const deeplinkValid = isSafeInternalDeeplink(deeplinkTrimmed);
  const canSend = !sending && title.trim() && message.trim() && deeplinkValid;

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error(tr('Başlık ve mesaj zorunludur.', 'Title and message are required.'));
      return;
    }
    if (!deeplinkValid) {
      toast.error(tr(
        'Bağlantı "/" ile başlayan uygulama-içi bir yol olmalı (ör. /courses).',
        'Link must be an internal path starting with "/" (e.g. /courses).'
      ));
      return;
    }
    setSending(true);
    setResult(null);
    setRateLimited(false);
    try {
      const body = {
        title: title.trim(),
        message: message.trim(),
        ...(deeplinkTrimmed ? { deeplink: deeplinkTrimmed } : {}),
      };
      const r = await axios.post(`${API}/admin/notify/test-self`, body, { headers: authHeaders() });
      setResult(r.data || {});
    } catch (err) {
      if (err?.response?.status === 429) {
        // slowapi'nin varsayılan RateLimitExceeded handler'ı {detail:...}
        // DEĞİL, {error: "..."} döner (message_tr/message_en yok) — bu yüzden
        // burada özel, anlaşılır bir TR/EN mesaj gösteriyoruz (ham hata basma).
        setRateLimited(true);
        toast.error(tr(
          'Saatlik test bildirimi limiti doldu (10/saat). Bir süre sonra tekrar deneyin.',
          'Hourly test notification limit reached (10/hour). Please try again later.'
        ));
      } else {
        const msg = err?.response?.data?.detail?.[`message_${lang}`]
          || err?.response?.data?.detail?.message_tr
          || err?.response?.data?.detail
          || (typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : null)
          || tr('Test bildirimi gönderilemedi.', 'Failed to send test notification.');
        toast.error(typeof msg === 'string' ? msg : tr('Test bildirimi gönderilemedi.', 'Failed to send test notification.'));
      }
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  // ── Push durum özeti render yardımcıları ────────────────────────────────────
  const deviceCount = status?.active_device_count ?? 0;
  const servicePushOn = status?.service_push_enabled;

  // ── Sonuç kartı metni (push_attempt_reason → anlaşılır TR/EN metin) ────────
  const resultCopy = (() => {
    if (!result) return null;
    const n = result.active_device_count ?? 0;
    switch (result.push_attempt_reason) {
      case 'sent':
        return {
          tone: 'success',
          icon: CheckCircle2,
          text: tr(
            `Bildirim oluşturuldu, ${n} cihaza push gönderildi.`,
            `Notification created, push sent to ${n} device(s).`
          ),
        };
      case 'no_active_tokens':
        return {
          tone: 'warning',
          icon: AlertTriangle,
          text: tr(
            'Bildirim oluşturuldu ama bu hesaba kayıtlı aktif cihaz yok. Mobil uygulamada Ayarlar > Bildirimler\'den izin verin.',
            'Notification created, but there is no active device registered for this account. Grant permission in the mobile app under Settings > Notifications.'
          ),
        };
      case 'no_permission_pref':
        return {
          tone: 'warning',
          icon: AlertTriangle,
          text: tr(
            'Bildirim oluşturuldu ama push tercihiniz kapalı. Mobil uygulamada Ayarlar > Bildirimler\'den açın.',
            'Notification created, but your push preference is turned off. Enable it in the mobile app under Settings > Notifications.'
          ),
        };
      default:
        return {
          tone: 'neutral',
          icon: Info,
          text: tr('Bildirim oluşturuldu.', 'Notification created.'),
        };
    }
  })();

  const TONE_CLS = {
    success: 'bg-emerald-50 border-emerald-300 text-emerald-800',
    warning: 'bg-amber-50 border-amber-300 text-amber-900',
    neutral: 'bg-sky-50 border-sky-300 text-sky-800',
  };

  return (
    <AnimatePresence>
      <motion.div
        key="push-self-test-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => !sending && onOpenChange(false)}
      >
        <motion.div
          key="push-self-test-box"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl border-2 border-[var(--border-light)] shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[var(--border-light)]">
            <div className="flex items-center gap-2">
              <BellRing size={18} className="text-cyan-600" />
              <h2 className="font-display text-base font-bold text-[var(--text-main)]">
                {tr('Push Bildirim Testi', 'Push Notification Test')}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => !sending && onOpenChange(false)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-[var(--text-muted)]"
              disabled={sending}
              aria-label={tr('Kapat', 'Close')}
            >
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-5">
            {/* ── Push durum özeti ─────────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide flex items-center gap-1.5">
                  <Smartphone size={13} /> {tr('Bu Hesabın Push Durumu', 'This Account\'s Push Status')}
                </span>
                <button
                  type="button"
                  onClick={fetchStatus}
                  disabled={statusLoading}
                  className="text-[var(--text-muted)] hover:text-[var(--text-main)] disabled:opacity-50"
                  aria-label={tr('Yenile', 'Refresh')}
                  data-testid="push-status-refresh"
                >
                  <RefreshCw size={13} className={statusLoading ? 'animate-spin' : ''} />
                </button>
              </div>

              {statusLoading ? (
                <div className="rounded-xl border-2 border-[var(--border-light)] p-3 text-xs font-semibold text-[var(--text-muted)] flex items-center gap-2" data-testid="push-status-loading">
                  <Loader2 size={13} className="animate-spin" /> {tr('Yükleniyor...', 'Loading...')}
                </div>
              ) : statusError ? (
                <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-3 text-xs font-semibold text-rose-700" data-testid="push-status-error">
                  {tr('Push durumu alınamadı — tekrar deneyin.', 'Could not fetch push status — try again.')}
                </div>
              ) : deviceCount === 0 ? (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="push-status-empty">
                  <p className="font-bold flex items-center gap-1.5">
                    <AlertTriangle size={13} /> {tr('Bu hesaba kayıtlı aktif cihaz yok.', 'No active device is registered for this account.')}
                  </p>
                  <p className="mt-1">
                    {tr(
                      'Push bildirimi almak için mobil uygulamayla giriş yapıp bildirim izni verin. Test bildirimi yine de gönderilebilir — ama hiçbir cihaza push ulaşmaz, sadece uygulama içi kayıt oluşur.',
                      'To receive push notifications, log in with the mobile app and grant notification permission. You can still send a test — but no device will receive push, only the in-app record is created.'
                    )}
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50 p-3 text-xs text-cyan-900" data-testid="push-status-summary">
                  <p className="font-bold">
                    {tr(`${deviceCount} aktif cihaz kayıtlı`, `${deviceCount} active device(s) registered`)}
                    {' — '}
                    {tr('iOS', 'iOS')}: {status?.platforms?.ios ?? 0}, {tr('Android', 'Android')}: {status?.platforms?.android ?? 0}
                  </p>
                  <p className="mt-1">
                    {servicePushOn
                      ? tr('Hizmet bildirimleri (push) bu hesapta AÇIK.', 'Service push notifications are ON for this account.')
                      : tr('Hizmet bildirimleri (push) bu hesapta KAPALI — test bildirimi oluşturulur ama push gitmez.', 'Service push notifications are OFF for this account — the test notification will be created but no push will be sent.')}
                  </p>
                  {status?.last_registered_at && (
                    <p className="mt-1 text-cyan-700">
                      {tr('Son cihaz kaydı', 'Last device registered')}: {new Date(status.last_registered_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ── Test formu ───────────────────────────────────────────────── */}
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                {tr('Kendine Test Bildirimi Gönder', 'Send a Test Notification to Yourself')}
              </p>
              <div>
                <label htmlFor="push-test-title" className="sr-only">{tr('Başlık', 'Title')}</label>
                <input
                  id="push-test-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                  disabled={sending}
                  placeholder={tr('Başlık', 'Title')}
                  className={INPUT_CLS}
                  data-testid="push-test-title"
                />
              </div>
              <div>
                <label htmlFor="push-test-message" className="sr-only">{tr('Mesaj', 'Message')}</label>
                <textarea
                  id="push-test-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                  disabled={sending}
                  placeholder={tr('Mesaj', 'Message')}
                  className={INPUT_CLS + ' resize-none'}
                  data-testid="push-test-message"
                />
              </div>
              <div>
                <label htmlFor="push-test-deeplink" className="sr-only">{tr('Bağlantı (opsiyonel)', 'Link (optional)')}</label>
                <input
                  id="push-test-deeplink"
                  value={deeplink}
                  onChange={(e) => setDeeplink(e.target.value)}
                  disabled={sending}
                  placeholder={tr('Bağlantı ekle (opsiyonel, ör. /courses)', 'Add link (optional, e.g. /courses)')}
                  className={INPUT_CLS + (deeplinkTrimmed && !deeplinkValid ? ' border-rose-400' : '')}
                  data-testid="push-test-deeplink"
                  aria-describedby={deeplinkTrimmed && !deeplinkValid ? 'push-test-deeplink-error' : undefined}
                />
                {deeplinkTrimmed && !deeplinkValid && (
                  <p id="push-test-deeplink-error" role="alert" className="text-[11px] text-rose-600 font-semibold mt-1">
                    {tr(
                      '"/" ile başlayan uygulama-içi bir yol olmalı — dış bağlantı kabul edilmez.',
                      'Must be an internal path starting with "/" — external links are not accepted.'
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* ── Sonuç kartı ──────────────────────────────────────────────── */}
            <div aria-live="polite">
              {resultCopy && (
                <div
                  className={`rounded-xl border-2 p-3 text-xs font-semibold flex items-start gap-2 ${TONE_CLS[resultCopy.tone]}`}
                  data-testid="push-test-result"
                >
                  <resultCopy.icon size={15} className="shrink-0 mt-0.5" />
                  <span>{resultCopy.text}</span>
                </div>
              )}
              {rateLimited && (
                <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-3 text-xs font-semibold text-rose-800 flex items-start gap-2 mt-2" data-testid="push-test-rate-limited">
                  <XCircle size={15} className="shrink-0 mt-0.5" />
                  <span>
                    {tr(
                      'Saatlik test bildirimi limiti doldu (10/saat). Bir süre sonra tekrar deneyin.',
                      'Hourly test notification limit reached (10/hour). Please try again later.'
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t-2 border-[var(--border-light)] bg-gray-50/60">
            <motion.button
              type="button"
              onClick={() => !sending && onOpenChange(false)}
              disabled={sending}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-4 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold text-[var(--text-main)] hover:bg-gray-100 disabled:opacity-40 bg-white"
            >
              {tr('Kapat', 'Close')}
            </motion.button>
            <motion.button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-cyan-500 text-white text-sm font-bold border-b-4 border-cyan-700 hover:bg-cyan-600 disabled:opacity-40"
              data-testid="push-test-send-btn"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? tr('Gönderiliyor...', 'Sending...') : tr('Test Bildirimi Gönder', 'Send Test Notification')}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
